/* global analytics */
import { FunctionComponent, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import ahoy from 'ahoy.js'

import useDispatch from '../../../hooks/useDispatch'
import useIsServer from '../../../hooks/useIsServer'
import useHideIntercom from '../../../hooks/useHideIntercom'
import {
  GetAdventureWithListingQueryResult,
  useGetAdventureWithListingLazyQuery,
  useGetSelectEscapeIndexesQuery,
} from '../../../hooks/useQueries'
import { useUtmParams } from '../../../utils/utmParams'

import SpinnerContainer from '../../../components/display/SpinnerContainer'
import Spinner from '../../../components/display/Spinner'
import ShareButton from '../../../components/display/ShareButton'
import Suggestions from '../../../search/Suggestions'
import JsonLd from '../../../components/utils/JsonLd'
import { adventureJsonLd } from '../../../components/utils/structuredData'
import AddToBucketList from '../../story/AddToBucketList'
import { HeadingXl } from '../../../components/display/Typography'
import { makeItemKey } from '../../../dashboard/MyBucketlists/gql/getBucketListItemsIndexHelpers'

import useLoginModalState from '../../../hooks/useLoginModalState'

import { openSelectEscape } from '../../../components/SelectEscape/reduxSlice'
import { setActionToReplay } from '../../../actions/authActions'

import CommonAdventureDetails from '../CommonAdventureDetails'
import { AdventureMapProps } from '../AdventureMap'
import { NarrowerContainer, Section } from '../shared'
import { PrerenderedAdventure, PrerenderedListing } from '../types'

import LinkToEntitiesNearLocation from './LinkToEntitiesNearLocation'
import NearbyAdventures from './NearbyAdventures'
import { normalizePrerenderedAdventure } from './helpers'

const trackAdventureViewed = (
  {
    id,
    adventureType,
    title,
  }: GetAdventureWithListingQueryResult['data']['adventure'],
  utmParams: Record<string, string> = {},
) => {
  analytics.track('Adventure Viewed', {
    adventure_id: id,
    adventure_title: title,
    adventure_type: adventureType.name,
    ...utmParams,
  })
  ahoy.track('Viewed adventure', {
    adventure_id: parseInt(id, 10),
  })
}

const idFromMatchId = (matchId: string) => parseInt(matchId, 10).toString()

interface AdventurePageProps {
  matchId: string
  prerenderedAdventure: PrerenderedAdventure
  prerenderedListing: PrerenderedListing
}

const AdventurePage: FunctionComponent<AdventurePageProps> = ({
  matchId,
  prerenderedAdventure,
  prerenderedListing,
}) => {
  const dispatch = useDispatch()
  const isServerSide = useIsServer()
  const [tracked, setTracked] = useState(false)
  const [hydrated, setHydrated] = useState(false)
  useHideIntercom()

  useEffect(() => {
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!isServerSide) {
      window.scrollTo(0, 0)
    }
  }, [])

  const id = idFromMatchId(matchId)

  const [loadGqlAdventureWithListing, { data: gqlAdventureData }] =
    useGetAdventureWithListingLazyQuery()

  const { loading: selectEscapeIndexesLoading, data: selectEscapeIndexesData } =
    useGetSelectEscapeIndexesQuery()

  useEffect(() => {
    if (
      !isServerSide &&
      (!prerenderedAdventure?.data ||
        prerenderedAdventure?.data?.id.toString() !== id)
    ) {
      loadGqlAdventureWithListing({ variables: { adventureId: id } })
    }
  }, [id, prerenderedAdventure, isServerSide])

  const { currentUserSignedIn, openLoginModal } = useLoginModalState()

  let adventureData =
    prerenderedAdventure?.data &&
    normalizePrerenderedAdventure(prerenderedAdventure.data)
  if (gqlAdventureData) {
    adventureData = gqlAdventureData.adventure
  }

  let listingData = {
    id: prerenderedListing?.data?.id?.toString(),
    state: prerenderedListing?.data?.state,
    region: {
      ...prerenderedListing?.data?.region,
      id: prerenderedListing?.data?.region?.id?.toString(),
    },
    region_name: prerenderedListing?.data?.region_name,
    market: {
      ...prerenderedListing?.data?.market,
      id: prerenderedListing?.data?.market?.id?.toString(),
    },
  }

  if (gqlAdventureData?.adventure?.__typename === 'StoryAdventure') {
    const gqlListing = gqlAdventureData.adventure.story.featuredListing
    listingData = {
      id: gqlListing?.id,
      state: gqlListing?.state,
      region: gqlListing?.region,
      region_name: gqlListing?.region?.name,
      market: gqlListing?.market,
    }
  }

  const utmParams = useUtmParams()

  useEffect(() => {
    if (adventureData && !tracked) {
      trackAdventureViewed(adventureData, utmParams)
      setTracked(true)
    }
  }, [adventureData, tracked])

  // eslint-disable-next-line eqeqeq -- intentional type coercion
  if (!adventureData || id != adventureData?.id) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  }

  const { location, title, shareUrl } = adventureData

  const bucketListItemsIndex = selectEscapeIndexesData?.currentUser
    ?.bucketListItemsIndex as GQL_BucketListItemsIndex
  const itemBucketListKey = makeItemKey({
    itemId: id.toString(),
    itemType: 'Adventure',
  })

  const bucketListIds =
    bucketListItemsIndex && (bucketListItemsIndex[itemBucketListKey] ?? [])
  const isInBucketList = bucketListIds?.length > 0

  const bookingAdventuresIndex = selectEscapeIndexesData?.currentUser
    ?.bookingAdventuresIndex as GQL_BookingAdventuresIndex
  const bookingIds =
    bookingAdventuresIndex && (bookingAdventuresIndex[id.toString()] ?? [])
  const isInBooking = bookingIds?.length > 0

  const handleSelectEscapeClick = () => {
    const openSelectEscapeAction = openSelectEscape({
      itemId: id.toString(),
      itemType: 'Adventure',
      source: 'adventure_page',
    })

    if (!currentUserSignedIn) {
      openLoginModal()
      dispatch(setActionToReplay(openSelectEscapeAction))
    } else {
      dispatch(openSelectEscapeAction)
    }
  }

  const actionNodes = (
    <>
      <AddToBucketList
        onClick={handleSelectEscapeClick}
        fetching={selectEscapeIndexesLoading}
        isActive={isInBucketList || isInBooking}
      />
      <ShareButton
        title={`Check out the ${title} adventure on Riparide`}
        url={shareUrl}
        noText={false}
        source="adventure_page"
      />
    </>
  )

  return (
    <>
      <JsonLd data={adventureJsonLd(adventureData)} />
      <CommonAdventureDetails
        adventure={adventureData as AdventureMapProps['adventureData']}
        actionNodes={actionNodes}
      >
        <NarrowerContainer>
          <Section>
            {/* SSR-seeded, so it renders server-side outside the hydration gate. */}
            {listingData?.id && (
              <Suggestions
                listing={listingData}
                prefetched
                customTitle={<HeadingXl>Places To Stay Nearby</HeadingXl>}
              />
            )}
            {hydrated && (
              <LinkToEntitiesNearLocation
                entityType="accommodation"
                location={location}
              />
            )}
          </Section>
          <Section>
            {/* SSR-seeded, so it renders server-side outside the hydration gate. */}
            <NearbyAdventures adventureId={id} prefetched />
            {hydrated && (
              <LinkToEntitiesNearLocation
                entityType="adventures"
                location={location}
              />
            )}
          </Section>
        </NarrowerContainer>
      </CommonAdventureDetails>
    </>
  )
}

interface ServerRenderedState {
  adventure: PrerenderedAdventure
  listing: PrerenderedListing
}

export default connect(
  (
    { adventure, listing }: ServerRenderedState,
    { match }: RouteComponentProps<{ id: string }>,
  ) => ({
    matchId: match.params.id,
    prerenderedAdventure: adventure,
    prerenderedListing: listing,
  }),
)(AdventurePage)
